import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy,
  OnInit, Output, SimpleChanges, ViewEncapsulation
} from '@angular/core';

import { Subscription } from 'rxjs';

import {
  BrandThemesEnum, DeviceService, ImageRatioEnum, PositionEnum, PushSizeEnum, SdBannerComponent,
  SdRichParagraphComponent, SdThemeComponent, SizesEnum, ThemesEnum
} from '@seb/web-design-system/core';

import type { IVisualCard } from './visual-card.interface';

@Component({
  selector: 'sd-visual-card',
  templateUrl: './visual-card.component.html',
  styleUrls: ['./visual-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [SdRichParagraphComponent, SdBannerComponent]
})
export class SdVisualCardComponent extends SdThemeComponent implements OnInit, OnChanges, OnDestroy {
  constructor(
    private deviceService: DeviceService,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }
  mediaAsBackground = true;
  isDesktopLarge$: boolean;
  isTablet$: boolean;
  isMobile$: boolean;
  desktopLargeSubscription: Subscription;
  tabletSubscription: Subscription;
  mobileSubscription: Subscription;
  themesEnum = ThemesEnum;
  sizesEnum = SizesEnum;
  pushSizeEnum = PushSizeEnum;
  positionEnum = PositionEnum;
  hasNoData = false;
  contentSize: SizesEnum;
  visualTextSize: SizesEnum.xl | SizesEnum.l | SizesEnum.m | SizesEnum.s = this.sizesEnum.s;
  imgRatio = ImageRatioEnum;

  @Input()
  isLoading = false;

  @Input()
  card: IVisualCard;

  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  @Input()
  titleLevel = 3;

  /**
   * Determines whether the title is inside a hx tag or in a p tag for the title
   */
  @Input()
  isDescription = false;

  /**
   * Allow to define de size of the visual card wanted
   * Changes content size
   */
  @Input()
  visualCardSize: PushSizeEnum = PushSizeEnum.m;

  @Input()
  hasModal = true;

  /**
   * Allow to define if the text is aligned to the top or the bottom, or even centered
   * * @param {string} left | right | center
   */
  @Input()
  layoutTextConfig:
    | PositionEnum.top
    | PositionEnum.bottom
    | PositionEnum.center = PositionEnum.center;

  /**
   * Allow to define either the theme is dark or light (default)
   * * @param {string} dark | light | brand | and marketing
   */
  @Input()
  themesConfig: ThemesEnum | BrandThemesEnum;

  @Input()
  invertTitle = false;

  @Input()
  twicPicsAutoSize = true;

  @Output()
  videoPlayed = new EventEmitter<{ status: string; title: string }>();

  @Output()
  videoEnded = new EventEmitter<{ status: string; title: string }>();

  @Input()
  class = ''; // override the standard class attr with a new one.
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.layoutTextConfig ? 'is-layout-' + this.layoutTextConfig : '',
      this.themesConfig ? 'is-theme-' + this.themesConfig : '',
      this.mediaAsBackground ? 'has-background' : '',
      this.invertTitle ? 'is-invert' : '',
      this.visualCardSize ? 'is-size-' + this.visualCardSize : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  ngOnInit() {
    this.desktopLargeSubscription = this.deviceService.isDesktopLarge.subscribe(
      (x) => {
        this.isDesktopLarge$ = x;
        this.cdRef.markForCheck();
      },
    );

    this.tabletSubscription = this.deviceService.isTablet.subscribe((x) => {
      this.isTablet$ = x;
      this.cdRef.markForCheck();
    });

    this.mobileSubscription = this.deviceService.isMobile.subscribe((x) => {
      this.isMobile$ = x;
      this.cdRef.markForCheck();
    });

    switch (this.visualCardSize) {
      case this.pushSizeEnum.l:
        this.visualTextSize = this.sizesEnum.xl;
        break;
      case this.pushSizeEnum.m:
        this.visualTextSize = this.sizesEnum.l;
        break;
      case this.pushSizeEnum.s:
        this.visualTextSize = this.sizesEnum.m;
        break;
      case this.pushSizeEnum.xs:
        this.visualTextSize = this.sizesEnum.m;
        break;
      default:
        this.visualTextSize = this.sizesEnum.s;
        break;
    }

    this.hasNoData = Object.entries(this.card?.content?.heading || {}).every(
      (x) => !!x[1]?.data?.label,
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    if (this.visualCardSize) {
      switch (this.visualCardSize) {
        case PushSizeEnum.m:
          this.contentSize = this.isMobile$
            ? SizesEnum.s
            : this.isTablet$
              ? SizesEnum.m
              : this.isDesktopLarge$
                ? SizesEnum.l
                : SizesEnum.m;
          break;
        case PushSizeEnum.s:
          this.contentSize = this.isMobile$
            ? SizesEnum.s
            : this.isTablet$
              ? SizesEnum.s
              : SizesEnum.m;
          break;
        case PushSizeEnum.xs:
          this.contentSize = this.isMobile$
            ? SizesEnum.s
            : this.isTablet$
              ? SizesEnum.s
              : SizesEnum.m;
          break;
        case PushSizeEnum.xxs:
          this.contentSize = SizesEnum.s;
          break;
      }
    }
  }

  ngOnDestroy() {
    this.mobileSubscription?.unsubscribe();
    this.tabletSubscription?.unsubscribe();
    this.desktopLargeSubscription?.unsubscribe();
  }

  onVideoPlayed(event: IVisualCard) {
    this.videoPlayed.emit({
      status: 'play',
      title: event.content?.heading?.data?.title
    });
  }

  onVideoEnded(event: IVisualCard) {
    this.videoEnded.emit({
      status: 'ended',
      title: event.content?.heading?.data?.title
    });
  }
}
